import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { getOrgLocale } from "../lib/locale";

const PHOTON = "https://photon.komoot.io/api/";
const AU_STATES = {
  "new south wales": "NSW", victoria: "VIC", queensland: "QLD", "south australia": "SA",
  "western australia": "WA", tasmania: "TAS", "northern territory": "NT", "australian capital territory": "ACT",
};

export const toParts = (p) => {
  const street = [p.housenumber, p.street || (p.housenumber ? "" : p.name)].filter(Boolean).join(" ");
  const suburb = p.city && p.district && p.osm_value !== "city" ? p.district : (p.locality || p.district || p.city || "");
  const state = p.countrycode === "AU" ? (AU_STATES[(p.state || "").toLowerCase()] || p.state || "") : (p.state || "");
  const postcode = p.postcode || "";
  const country = p.country || "";
  const placeName = p.street && p.name && p.name !== p.street && !p.housenumber ? `${p.name}, ` : "";
  const home = getOrgLocale().country;
  const full = `${placeName}${[street, suburb, [state, postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ")}${country && p.countrycode !== home ? `, ${country}` : ""}`;
  return { street: street || p.name || "", suburb, state, postcode, country, countrycode: p.countrycode, full };
};

export function AddressAutocomplete({ value, onChange, onSelect, placeholder = "Start typing an address", className = "", inputComponent: Comp = "input", testid = "address-autocomplete", disabled }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const typed = useRef(false);
  const box = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (!typed.current || q.length < 4) { setItems([]); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      fetch(`${PHOTON}?q=${encodeURIComponent(q)}&limit=10&lang=en`, { signal: ctrl.signal })
        .then((r) => r.json())
        .then((j) => {
          const home = getOrgLocale().country;
          const feats = (j.features || []).map((f) => f.properties).filter((p) => p.street || p.housenumber || p.name);
          feats.sort((a, b) => (b.countrycode === home) - (a.countrycode === home));
          const seen = new Set();
          const list = feats.map(toParts).filter((x) => x.full && !seen.has(x.full) && seen.add(x.full)).slice(0, 6);
          setItems(list); setOpen(list.length > 0); setActive(-1);
        })
        .catch(() => { setItems([]); });
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [value]);

  useEffect(() => {
    const off = (e) => { if (box.current && !box.current.contains(e.target)) { setOpen(false); } };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);

  const pick = (it) => { typed.current = false; setOpen(false); setItems([]); onSelect(it); };
  const onKey = (e) => {
    if (!open) { return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
    if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={box}>
      <Comp value={value || ""} disabled={disabled} placeholder={placeholder} className={className} autoComplete="off" data-testid={testid}
        onChange={(e) => { typed.current = true; onChange(e.target.value); }} onKeyDown={onKey} onFocus={() => items.length && setOpen(true)} />
      {open && (
        <ul className="absolute z-[60] left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto text-sm" data-testid={`${testid}-options`}>
          {items.map((it, i) => (
            <li key={it.full}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(it); }} onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3 py-2 flex items-start gap-2 transition-colors ${i === active ? "bg-sky-50" : "hover:bg-slate-50"}`} data-testid={`${testid}-option-${i}`}>
                <MapPin className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
                <span>{it.full}</span>
              </button>
            </li>
          ))}
          <li className="px-3 py-1 text-[10px] text-slate-400 border-t">Suggestions © OpenStreetMap contributors</li>
        </ul>
      )}
    </div>
  );
}
